import { FC, useEffect, useState } from 'react';

import { VisuallyHidden } from '@purpur/library';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ProductSearchItem, SearchQueryResponse } from '@telialt/core-search/src/model';
import { withParams } from '@telialt/utils/src/helpers';

type Props = {
    isLoading?: boolean;
    products?: SearchQueryResponse<ProductSearchItem>;
};

export const A11yProductsAnnouncer: FC<Props> = ({ isLoading, products }) => {
    const { t } = useTranslations();

    const [announcementText, setAnnouncementText] = useState<string>(null);
    const loadingText = t['common.loading'];
    const productsText = t['eshop.category.products_a11y_label'];
    const productsNotFoundText = t['eshop.category.products_not_found_a11y_label'];

    useEffect(() => {
        if (isLoading) {
            setAnnouncementText(loadingText);
        } else if (products?.items?.length) {
            const productLabels = products.items.map((product) => product.name).join(', ');
            setAnnouncementText(
                withParams(productsText, {
                    count: products.items.length,
                    totalCount: products.total,
                    productLabels,
                }),
            );
        } else if (products?.total === 0) {
            setAnnouncementText(productsNotFoundText);
        } else {
            setAnnouncementText(null);
        }

        const timeoutHandle = window.setTimeout(() => setAnnouncementText(null), 2000);

        return () => {
            clearTimeout(timeoutHandle);
        };
    }, [isLoading, loadingText, products, productsNotFoundText, productsText]);

    return (
        <VisuallyHidden>
            <div aria-live="polite" aria-atomic="true" aria-hidden={!announcementText}>
                {announcementText}
            </div>
        </VisuallyHidden>
    );
};
